import { Button } from '@/components/ui/button';
import { useMemo, useState } from "react";
import { Link, useLocation as useWouterLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { apiRequest } from '@/lib/fetchWithAuth';
import { omitUnchangedLoginEmail } from '@shared/profile-edit';
import { profileSaveErrorMessage } from '@shared/profile-save-feedback';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine
} from 'recharts';

interface SettingsProps {
  user: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
    isSuperAdmin?: boolean;
    hierarchyLevel?: string;
  };
}

export default function Settings({ user }: SettingsProps) {
  const [, setLocation] = useWouterLocation();
  const { currentOrganizationId } = useOrganization();
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email);
  const [savedEmail, setSavedEmail] = useState(user.email);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "error" | "success">("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const currentYear = new Date().getFullYear();
  const [authError, setAuthError] = useState(false);
  const canUpgrade = Boolean(user?.isSuperAdmin) || String(user?.role || '').toLowerCase() === 'ceo' || String(user?.hierarchyLevel || '').toLowerCase() === 'ceo';

  // Tier color mapping for badge visuals
  const TIER_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    Free: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-700' },
    Starter: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-700' },
    Professional: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-700' },
    Business: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-700' },
    Enterprise: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-700' },
  };

  // Usage: current month snapshot
  type CurrentUsage = {
    organizationId: string;
    month: string;
    fileCount: number;
    totalBytes: number;
    totalGb: number;
    ratePerGb: number;
    priceUsd: number;
    tier?: string;
    tierLimitGb?: number;
    usagePercent?: number;
    remainingGb?: number;
    tierTotalCost?: number;
    [key: string]: any;
  };

  const { data: currentUsage } = useQuery<CurrentUsage>({
    queryKey: ['/api/usage/storage/current', currentOrganizationId],
    queryFn: async () => {
      const url = `/api/usage/storage/current${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ''}`;
      const res = await fetch(url, { credentials: 'include', redirect: 'error' });
      if (res.status === 401) {
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to load current usage');
      return res.json() as Promise<CurrentUsage>;
    },
    enabled: !!currentOrganizationId && !authError,
    refetchInterval: authError ? false : 60000,
    staleTime: 0,
    retry: (failureCount, error: any) => {
      if (String(error?.message).includes('Unauthorized')) return false;
      return failureCount < 2;
    },
    // handle onError via retry/flags only (option removed to satisfy TS)
  });

  // Usage: monthly for selected year (defaults to current year)
  const { data: yearly } = useQuery({
    queryKey: ['/api/usage/storage', currentOrganizationId, currentYear],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('year', String(currentYear));
      if (currentOrganizationId) qs.set('organizationId', currentOrganizationId);
      const res = await fetch(`/api/usage/storage?${qs.toString()}`, { credentials: 'include', redirect: 'error' });
      if (res.status === 401) {
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to load yearly usage');
      return res.json() as Promise<{ year: number; ratePerGb: number; months: { month: string; fileCount: number; totalBytes: number; totalGb: number; priceUsd: number }[]; ytdAvgGb: number; ytdAvgCost: number }>;
    },
    enabled: !!currentOrganizationId && !authError,
    refetchInterval: authError ? false : 60000,
    staleTime: 0,
    retry: (failureCount, error: any) => {
      if (String(error?.message).includes('Unauthorized')) return false;
      return failureCount < 2;
    },
    // onError removed; handled by retry/authError flag
  });

  const chartData = useMemo(() => (yearly?.months || []).map(m => ({
    name: m.month.slice(5,7),
    gb: m.totalGb,
    cost: m.priceUsd,
  })), [yearly]);

  const handleDownloadCsv = async () => {
    try {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const qs = new URLSearchParams();
      if (currentOrganizationId) qs.set('organizationId', currentOrganizationId);
      qs.set('year', String(currentYear));
      qs.set('month', month);
      const res = await fetch(`/api/usage/storage/latest-csv?${qs.toString()}`, { credentials: 'include', redirect: 'error' });
      if (!res.ok) throw new Error('CSV not available');
      const payload = await res.json();
      if (payload?.url) {
        window.open(payload.url, '_blank');
      }
    } catch (e) {
      alert('CSV not available yet. Try again after the nightly rollup.');
    }
  };

  const handleSave = async () => {
    if (newPassword && newPassword !== confirmPassword) {
      setStatus("error");
      setSaveMessage("Passwords do not match.");
      return;
    }

    setStatus("saving");
    setSaveMessage("");

    try {
      const result = await apiRequest<{ emailVerificationRequired?: boolean }>(`/api/users/${user.id}`, {
        method: "PATCH",
        data: omitUnchangedLoginEmail({
          fullName,
          email,
          ...(newPassword ? { password: newPassword } : {}),
        }, savedEmail),
      });

      setSavedEmail(email);
      setStatus("success");
      setNewPassword("");
      setConfirmPassword("");
      setSaveMessage(result?.emailVerificationRequired === true
        ? "Profile saved. Check your email for a verification code before your next sign-in."
        : "Profile updated successfully!");
    } catch (error) {
      setStatus("error");
      setSaveMessage(profileSaveErrorMessage(error));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Settings</h1>

      {/* HIPAA banner */}
      <div className="mb-4 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-100 px-4 py-3 text-sm">
        HIPAA Notice: BoltOS Settings include storage and audit features designed for regulated healthcare environments.{' '}
        <a href="/compliance" className="underline text-amber-900 dark:text-amber-100">Compliance Details</a>
      </div>

      {authError && (
        <div className="mb-4 rounded border border-red-700 bg-red-900/20 text-red-800 px-4 py-3 text-sm">
          Your session has expired. Please refresh the page or log in again. Data auto-refresh is paused to avoid errors.
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Full Name</label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full px-4 py-2 rounded bg-white dark:bg-neutral-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-neutral-600"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 rounded bg-white dark:bg-neutral-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-neutral-600"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">New Password</label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-4 py-2 rounded bg-white dark:bg-neutral-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-neutral-600"
          placeholder="Leave blank to keep current password"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Confirm New Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full px-4 py-2 rounded bg-white dark:bg-neutral-900 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-neutral-600"
        />
      </div>

      <Button
        onClick={handleSave}
        disabled={status === "saving"}
        className="px-6 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
      >
        {status === "saving" ? "Saving..." : "Save Changes"}
      </Button>

      {saveMessage && (
        <p role={status === "error" ? "alert" : "status"} className="mt-3 text-sm">
          {saveMessage}
        </p>
      )}

      <div className="h-px bg-slate-100 dark:bg-neutral-800 my-8" />

      <h2 className="text-xl font-semibold mb-3">Storage Usage</h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
        Auto-updating every 60s. Pricing uses ${currentUsage?.ratePerGb?.toFixed?.(2) ?? '0.15'}/GB-month (subject to contract).
      </p>

      {/* Tiered progress bar */}
      {currentUsage && (
        <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${TIER_COLORS[currentUsage.tier || 'Free']?.bg} ${TIER_COLORS[currentUsage.tier || 'Free']?.border}`}>
              <span className={`text-xs font-medium ${TIER_COLORS[currentUsage.tier || 'Free']?.text}`}>{currentUsage.tier || '—'} Plan</span>
            </div>
            <div className="text-slate-600 dark:text-slate-300 text-sm font-medium">{currentUsage.totalGb?.toFixed?.(2) ?? 0} GB / {currentUsage.tierLimitGb === Infinity ? '∞' : `${currentUsage.tierLimitGb} GB`}</div>
          </div>
          <div className="w-full bg-slate-100 dark:bg-neutral-800 rounded-full h-4 overflow-hidden border border-slate-300 dark:border-neutral-600">
            <div
              className={`h-full rounded-full transition-all duration-700 ${(currentUsage?.usagePercent ?? 0) < 60 ? 'bg-gradient-to-r from-green-600 to-green-400' : (currentUsage?.usagePercent ?? 0) < 80 ? 'bg-gradient-to-r from-yellow-600 to-yellow-400' : 'bg-gradient-to-r from-red-600 to-red-400'}`}
              style={{ width: `${Math.min(currentUsage?.usagePercent ?? 0, 100)}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2 text-xs text-neutral-500 dark:text-neutral-300">
            <span>{(currentUsage?.usagePercent ?? 0).toFixed(1)}% used</span>
            <span>{currentUsage.remainingGb != null ? `${currentUsage.remainingGb.toFixed?.(1)} GB remaining` : 'Unlimited'}</span>
          </div>
          {(currentUsage?.usagePercent ?? 0) > 80 && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${(currentUsage?.usagePercent ?? 0) > 90 ? 'bg-red-500/10 border border-red-500/30 text-red-800' : 'bg-yellow-500/10 border border-yellow-500/30 text-amber-800'}`}>
              {(currentUsage?.usagePercent ?? 0) > 90 ? 'Critical: storage nearly full. Upgrade now to avoid interruption.' : 'Warning: storage nearing limit. Consider upgrading your plan.'}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-slate-600 dark:text-slate-300 text-sm">Current Month (GB)</div>
            <div aria-hidden>💾</div>
          </div>
          <div className="text-3xl font-semibold">{currentUsage ? currentUsage.totalGb.toFixed(2) : '—'}</div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-slate-600 dark:text-slate-300 text-sm">Files</div>
            <div aria-hidden>📁</div>
          </div>
          <div className="text-3xl font-semibold">{currentUsage ? currentUsage.fileCount : '—'}</div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-slate-600 dark:text-slate-300 text-sm">Projected Bill ($)</div>
            <div aria-hidden>💰</div>
          </div>
          <div className="text-3xl font-semibold">{currentUsage ? (currentUsage.tierTotalCost ?? currentUsage.priceUsd).toFixed(2) : '—'}</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-300 mt-1">{currentUsage?.tier || '—'} plan</div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-slate-600 dark:text-slate-300 text-sm">YTD Avg (GB)</div>
            <div aria-hidden>📊</div>
          </div>
          <div className="text-3xl font-semibold">{yearly ? yearly.ytdAvgGb.toFixed(1) : '—'}</div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
          <div className="text-slate-800 dark:text-slate-100 font-medium">{currentYear} Monthly Usage (GB)</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleDownloadCsv} className="px-3 py-1.5 text-sm rounded bg-slate-100 dark:bg-neutral-800 hover:bg-slate-200 dark:hover:bg-neutral-700 border border-slate-300 dark:border-neutral-600">Download last CSV</Button>
            {(canUpgrade || (currentUsage?.usagePercent ?? 0) > 60) && (
              <Button
                onClick={() => {
                  try { setLocation('/billing/upgrade'); } catch {}
                }}
                className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white"
              >
                Upgrade
              </Button>
            )}
            {/* CEO Storage Manager opens modal to view/delete top files */}
            <Button asChild variant="outline"><a href="/settings/storage-manager" className="px-3 py-1.5 text-sm rounded bg-slate-100 dark:bg-neutral-800 hover:bg-slate-200 dark:hover:bg-neutral-700 border border-slate-300 dark:border-neutral-600">BoltOS Storage Manager</a></Button>
            {/* Add Recovery Vault */}
            <Button asChild variant="outline"><a href="/billing/recovery-tiers" className="px-3 py-1.5 text-sm rounded bg-teal-600 hover:bg-teal-500 text-white">Add Recovery Vault</a></Button>
          </div>
        </div>
        <div className="w-full h-64" data-testid="storage-usage-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 80, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, currentUsage?.tierLimitGb || 'auto']} />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.03)' }} contentStyle={{ background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 6 }} />
              {currentUsage?.tierLimitGb && currentUsage.tierLimitGb !== Infinity && (
                <ReferenceLine y={currentUsage.tierLimitGb} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `${currentUsage.tier} limit`, position: 'right', fill: '#475569', fontSize: 10 }} />
              )}
              <Bar dataKey="gb" fill="#22c55e" name="GB" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-300 mt-2">
          YTD Avg: {yearly ? yearly.ytdAvgGb.toFixed(2) : '—'} GB · Est ${yearly ? yearly.ytdAvgCost.toFixed(2) : '—'}/month
        </div>
      </div>
    </div>
  );
}
